// Componente Header - Navegación principal de la aplicación
// Basado en el mockup_ejemplo.html con efecto glassmorphism

import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { usePermissions } from '../hooks/usePermissions'
import { useCart } from '../hooks/useCart'
import Logo from './Logo'
import './Header.css'
import { FIXED_ROLES } from '../constants/permissions'

/**
 * Componente Header
 * Header fijo con efecto glassmorphism, navegación y acciones
 */
function Header() {
  // Hook de autenticación
  const { currentUser, logout, userData, customRole, loadUserData } = useAuth()
  const { hasAdminAccess, getNormalizedRole } = usePermissions()
  
  // Hook del carrito
  const { getCartItemCount } = useCart()
  
  // Navegación
  const navigate = useNavigate()
  
  // Obtener número de items en el carrito
  const cartItemsCount = getCartItemCount()
  const normalizedRole = getNormalizedRole()
  const customRoleHasPermissions = !!customRole?.permissions && Object.keys(customRole.permissions).length > 0
  const showAdmin = hasAdminAccess()
    || normalizedRole === FIXED_ROLES.ADMIN
    || normalizedRole === FIXED_ROLES.SUPERADMIN
    || customRoleHasPermissions
  
  // Estado para el menú de usuario (dropdown)
  const [showUserMenu, setShowUserMenu] = useState(false)
  
  // Estado para el menú móvil (hamburguesa)
  const [showMobileMenu, setShowMobileMenu] = useState(false)

  useEffect(() => {
    if (currentUser && !userData) {
      loadUserData(currentUser.uid)
    }
  }, [currentUser, userData, loadUserData])

  // Efecto para cerrar el menú de usuario al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu && !event.target.closest('.user-menu-container')) {
        setShowUserMenu(false)
      }
    }

    if (showUserMenu) {
      document.addEventListener('click', handleClickOutside)
    }

    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [showUserMenu])

  // Efecto para cerrar el menú móvil al hacer scroll
  useEffect(() => {
    const handleScroll = () => {
      if (showMobileMenu) {
        setShowMobileMenu(false)
      }
    }

    if (showMobileMenu) {
      window.addEventListener('scroll', handleScroll)
    }

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [showMobileMenu])

  // Función para hacer scroll suave a una sección
  const handleSmoothScroll = (e, targetId) => {
    e.preventDefault()
    const element = document.querySelector(targetId)
    if (element) {
      // Calcular offset para compensar el header fijo
      const headerHeight = 90
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset
      const offsetPosition = elementPosition - headerHeight

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      })
    }
  }

  return (
    <header className="sticky top-0 z-50 w-full border-b border-gray-100 bg-background-light dark:bg-background-dark dark:border-gray-800 px-4 md:px-10 py-4 md:py-5 app-header">
      <div className="mx-auto flex max-w-[1280px] items-center justify-between gap-2 md:gap-8 nav-container flex-nowrap">
        {/* Logo Section */}
        <Link to="/" className="flex items-center gap-4 text-text-dark dark:text-text-light logo-link min-w-0 h-12 md:h-14" onClick={() => setShowMobileMenu(false)}>
          <Logo className="h-full w-auto logo-icon object-contain" />
        </Link>
        
        {/* Navigation Links */}
        <nav className={`nav-links-container ${showMobileMenu ? 'mobile-active' : ''}`}>
          <Link to="/" className="nav-link" onClick={(e) => { setShowMobileMenu(false); if(window.location.pathname === '/') { e.preventDefault(); handleSmoothScroll(e, '#inicio'); }}}>Inicio</Link>
          <Link to="/productos" className="nav-link" onClick={() => setShowMobileMenu(false)}>Productos</Link>
          <a href="#categorias" className="nav-link" onClick={(e) => { setShowMobileMenu(false); if(window.location.pathname === '/') { handleSmoothScroll(e, '#categorias'); } else { e.preventDefault(); window.location.href = '/#categorias'; }}}>Categorías</a>
          <a href="#ofertas" className="nav-link" onClick={(e) => { setShowMobileMenu(false); if(window.location.pathname === '/') { handleSmoothScroll(e, '#ofertas'); } else { e.preventDefault(); window.location.href = '/#ofertas'; }}}>Ofertas</a>
          <a href="#contacto" className="nav-link" onClick={(e) => { setShowMobileMenu(false); if(window.location.pathname === '/') { handleSmoothScroll(e, '#contacto'); } else { e.preventDefault(); window.location.href = '/#contacto'; }}}>Contacto</a>

          {/* Mobile Only: Separator & Extra Menu Items */}
          <div className="h-px w-full bg-gray-100 md:hidden my-2"></div>

          {/* Mobile Only: Cart */}
          <Link to="/carrito" className="nav-link flex items-center gap-3 md:hidden" onClick={() => setShowMobileMenu(false)}>
            <div className="relative">
              <span className="material-symbols-outlined">shopping_cart</span>
              {cartItemsCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] text-white font-bold">
                  {cartItemsCount}
                </span>
              )}
            </div>
            Carrito de Compras
          </Link>

          {/* Mobile Only: User Actions */}
          {currentUser ? (
            <>
              <Link to="/perfil" className="nav-link flex items-center gap-3 md:hidden" onClick={() => setShowMobileMenu(false)}>
                <span className="material-symbols-outlined">account_circle</span>
                Mi Perfil
              </Link>
              <Link to="/mis-pedidos" className="nav-link flex items-center gap-3 md:hidden" onClick={() => setShowMobileMenu(false)}>
                <span className="material-symbols-outlined">inventory_2</span>
                Mis Pedidos
              </Link>
              {showAdmin ? (
                <Link to="/admin" className="nav-link flex items-center gap-3 md:hidden" onClick={() => setShowMobileMenu(false)}>
                  <span className="material-symbols-outlined">admin_panel_settings</span>
                  Panel Admin
                </Link>
              ) : null}
              <button 
                className="nav-link flex w-full items-center gap-3 text-red-600 md:hidden text-left"
                onClick={async () => {
                  await logout()
                  setShowMobileMenu(false)
                }}
              >
                <span className="material-symbols-outlined">logout</span>
                Cerrar Sesión
              </button>
            </>
          ) : (
            <Link to="/login" className="nav-link flex items-center gap-3 md:hidden" onClick={() => setShowMobileMenu(false)}>
              <span className="material-symbols-outlined">login</span>
              Iniciar Sesión
            </Link>
          )}
        </nav>

        {/* Right Section: Search & Actions */}
        <div className="flex flex-1 justify-end gap-4 md:gap-8 nav-actions-container">
          {/* Search Bar (Desktop) */}
          <label className="hidden md:flex flex-col min-w-40 !h-10 max-w-64 search-bar-container">
            <div className="flex w-full flex-1 items-stretch rounded-lg h-full bg-accent-light dark:bg-gray-800 search-bar-inner">
              <div className="text-primary flex border-none items-center justify-center pl-4 rounded-l-lg border-r-0">
                <span className="material-symbols-outlined text-[20px]">search</span>
              </div>
              <input className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-text-dark dark:text-text-light focus:outline-0 focus:ring-0 border-none bg-transparent focus:border-none h-full placeholder:text-gray-400 placeholder:font-light px-4 rounded-l-none border-l-0 pl-2 text-sm font-normal leading-normal tracking-tight" placeholder="Buscar muebles..." />
            </div>
          </label>

          {/* Action Buttons */}
          <div className="flex gap-2 action-buttons flex-row items-center flex-nowrap">
            {/* Cart Button (Desktop Only) */}
            <Link to="/carrito" className="hidden md:flex items-center justify-center overflow-hidden rounded-lg size-10 bg-accent-light dark:bg-gray-800 text-text-dark dark:text-text-light hover:bg-primary/20 transition-colors relative flex-shrink-0">
              <span className="material-symbols-outlined text-[20px]">shopping_cart</span>
              {cartItemsCount > 0 && (
                <span className="absolute top-1 right-1 flex h-3 w-3 items-center justify-center rounded-full bg-red-500 text-[10px] text-white">
                  {cartItemsCount}
                </span>
              )}
            </Link>

            {/* User Button / Menu (Desktop Only) */}
            <div className="hidden md:block relative user-menu-container flex-shrink-0">
              <button 
                className="flex items-center justify-center overflow-hidden rounded-lg size-10 bg-accent-light dark:bg-gray-800 text-text-dark dark:text-text-light hover:bg-primary/20 transition-colors p-0"
                onClick={() => currentUser ? setShowUserMenu(!showUserMenu) : navigate('/login')}
              >
                {currentUser && (currentUser.photoURL || userData?.photoURL) ? (
                  <img 
                    src={currentUser.photoURL || userData?.photoURL} 
                    alt={userData?.displayName || 'Usuario'} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="material-symbols-outlined text-[20px]">person</span>
                )}
              </button>

              {/* User Dropdown */}
              {showUserMenu && currentUser && (
                <div className="absolute right-0 top-full mt-2 w-64 rounded-xl border border-gray-100 bg-white p-2 shadow-lg z-50 user-dropdown">
                  <div className="px-4 py-3">
                    <p className="text-sm font-medium text-gray-900">{userData?.displayName || 'Usuario'}</p>
                    <p className="text-xs text-gray-500">{currentUser.email}</p>
                  </div>
                  <div className="h-px bg-gray-100 my-1"></div>
                  <Link to="/perfil" className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" onClick={() => setShowUserMenu(false)}>
                    <span className="material-symbols-outlined text-[18px]">account_circle</span>
                    Mi Perfil
                  </Link>
                  <Link to="/mis-pedidos" className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" onClick={() => setShowUserMenu(false)}>
                    <span className="material-symbols-outlined text-[18px]">inventory_2</span>
                    Mis Pedidos
                  </Link>
                   {showAdmin ? (
                      <Link to="/admin" className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" onClick={() => setShowUserMenu(false)}>
                         <span className="material-symbols-outlined text-[18px]">admin_panel_settings</span>
                         Panel Admin
                      </Link>
                    ) : null}
                  <div className="h-px bg-gray-100 my-1"></div>
                  <button 
                    className="flex w-full items-center gap-2 rounded-lg px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    onClick={async () => {
                      await logout()
                      setShowUserMenu(false)
                    }}
                  >
                    <span className="material-symbols-outlined text-[18px]">logout</span>
                    Cerrar Sesión
                  </button>
                </div>
              )}
            </div>

            {/* Mobile Menu Toggle */}
            <button 
              className="md:hidden flex items-center justify-center overflow-hidden rounded-lg size-10 bg-accent-light dark:bg-gray-800 text-text-dark dark:text-text-light hover:bg-primary/20 transition-colors flex-shrink-0"
              onClick={() => setShowMobileMenu(!showMobileMenu)}
            >
              <span className="material-symbols-outlined text-[20px]">{showMobileMenu ? 'close' : 'menu'}</span>
            </button>
          </div>
        </div>
      </div>
    </header>
  )
}

export default Header

